import { ViewportScroller } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Inject,
    Input,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, map, combineLatest, filter } from 'rxjs';

import { getHomeConfig, getPage, getStore } from '@core/infrastructure/state/core.selectors';
import { isCategoryPage, isHomePage, isSearchPage } from '@features/category/application/services/category.utils';
import { Resolutions } from '@layout/domain/models/layout.models';
import { OpenSearchFromHeader } from '@layout/infrastructure/state/layout.actions';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { SearchSection, SearchSelectors } from '@pages/search';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { AnalyticsClickSourceID } from '@shared/analytics/COMMON/models/analytics-common.models';
import { HeaderEventAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { MenuCategory } from '@shared/header/models/category.model';
import { CategorySections } from '@shared/header/models/header.models';
import { CloseMenu, OpenMenu } from '@shared/menu-categories/store/menu-categories.actions';
import { getLastSectionSelector, isMenuOpen } from '@shared/menu-categories/store/menu-categories.selectors';
import { GoToHome, GoToSection, PublicNavigation } from '@shared/navigation';
import {
    PublicImpl as ShopCartPublicImpl,
    PublicTokens as ShopCartPublicTokens,
    PublicPorts as ShopCartPublicPorts,
} from '@shared/shopcart/infrastructure/public/public-api';
import { getItemsInCart } from '@shared/shopcart-core/infrastructure/state/shopcart-core.selectors';
import { selectUserName, selectUserIsLogged } from '@shared/user/store';

@Component({
    selector: 'header-topbar',
    templateUrl: './header-topbar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: ShopCartPublicTokens.SHOPPING_CART_SIDEBAR_FACADE,
            useFactory: (store) => new ShopCartPublicImpl.ShoppingCartSidebarFacade(store),
            deps: [Store],
        },
    ],
})
export class HeaderTopbarComponent {
    @Input() public categories: MenuCategory[];

    @Input() public isLogged = false;

    @Input() public direction: string;

    @Input() public lastSection: string;

    @ViewChild('container') public containerClassManager?: ElementRef;

    public cartItem$: Observable<number> = this.store.select(getItemsInCart).pipe(
        map((coreItemsInCart) => {
            return coreItemsInCart ?? 0;
        })
    );

    public resolution$: Observable<string> = this.store.select(getResolution).pipe(
        map((resolution) => {
            return resolution === Resolutions.MOBILE || resolution === Resolutions.TABLET ? 'mobile' : 'desktop';
        })
    );

    public latestHomeSeoUrl$: Observable<string> = combineLatest([
        this.store.select(getHomeConfig),
        this.store.select(getLastSectionSelector),
    ]).pipe(
        map(([configs, section]) => configs.find(config => config.section === section)),
        filter(config => !!config && config.locationPath !== ''),
        map(config => config.locationPath)
    );

    public sections: string[] = CategorySections;

    public lastSection$ = this.store.select(getLastSectionSelector);

    public section$: Observable<SearchSection> = this.store.select(SearchSelectors.getSearchSection);

    public isHomePage$: Observable<boolean> = this.store.select(getPage).pipe(
        map((page) => {
            return isHomePage(page);
        })
    );

    public isCategoryPage$: Observable<boolean> = this.store.select(getPage).pipe(
        map((page) => {
            return isCategoryPage(page);
        })
    );

    public isSearchPage$: Observable<boolean> = this.store.select(getPage).pipe(
        map((page) => isSearchPage(page))
    );

    public isOpenForSale$: Observable<boolean> = this.store.select(getStore).pipe(
        filter((storeData) => !!storeData),
        map((storeData) => storeData.isOpenForSale)
    );

    public isAuthenticated$: Observable<boolean> = this.store.select(selectUserIsLogged)
        .pipe(filter((isLogged) => typeof isLogged === 'boolean'));

    public loginHref: string;

    public myAccountHref: string;

    public username$: Observable<string> = this.store.select(selectUserName).pipe(map(name => {
        const trimmedName = name?.trim();
        return trimmedName !== '-' ? trimmedName : null;
    }));

    public isOpenMenu$: Observable<boolean> = this.store.select(isMenuOpen);

    private readonly isTheMiniCartComponentRegisteredInSidebar = false;

    protected readonly ANALYTICS_CLICK_SOURCE_ID_VIEW_MINICART = AnalyticsClickSourceID.headerViewMinicart;

    constructor(
        private readonly store: Store,
        private readonly viewportScroller: ViewportScroller,
        private readonly abTestingService: ABTestingService,
        private readonly routerNavigation: PublicNavigation,
        @Inject(ShopCartPublicTokens.SHOPPING_CART_SIDEBAR_FACADE)
        private readonly shoppingCartSidebarFacade: ShopCartPublicPorts.IShoppingCartSidebarCommandFacade,
    ) {
        this.loginHref = this.routerNavigation.getCompleteUrlBySection('userLoginApolo');
        this.myAccountHref = this.routerNavigation.getCompleteUrlBySection('myAccount');
    }

    public openMenu(): void {
        if (typeof hj !== 'undefined') {
            hj('tagRecording', ['open_menu_sidebar']);
        }
        this.store.dispatch(OpenMenu());
    }

    public closeMenu(): void {
        this.store.dispatch(CloseMenu());
    }

    public goToHome(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.store.dispatch(HeaderEventAnalytics({ section: 'logo' }));
        this.store.dispatch(GoToHome({}));
        this.viewportScroller.scrollToPosition([0, 0]);
    }

    public openSearch(): void {
        this.store.dispatch(OpenSearchFromHeader({ from: 'header' }));
    }

    public openLogin(): void {
        this.store.dispatch(HeaderEventAnalytics({ section: 'login' }));
        this.store.dispatch(GoToSection({ url: 'userLoginApolo' }));
    }

    public openMyAccount(): void {
        this.store.dispatch(HeaderEventAnalytics({ section: 'account' }));
        this.store.dispatch(GoToSection({ url: 'myAccount', reload: true }));
    }

    public openShopCart(_event: Event): void {
        this.store.dispatch(HeaderEventAnalytics({ section: 'cart' }));
        // Test AB ECOMDUTI-16388 Reducir pasos minicesta
        if (this.abTestingService.getShopCartStepVariant('goToShopCartFromHeaderButton')) {
            this.store.dispatch(GoToSection({ url: 'shopCart', reload: true }));
            return;
        }

        this.shoppingCartSidebarFacade.openMiniCartInSidebar();
    }
}
